import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Title, Text } from '@siftscience/focus-components/text'
import { Checkbox } from '@siftscience/focus-components/checkbox'
import { Button, IconButton } from '@siftscience/focus-components/button'
import { Delete } from '@siftscience/focus-components/icons/Delete'
import { Eye } from '@siftscience/focus-components/icons/Eye'
import { Refresh } from '@siftscience/focus-components/icons/Refresh'
import { ImageCardProps } from '../types'

const useStyles = makeStyles(() => ({
  container: {
    height: 'auto',
    width: '100%',
    borderRadius: '8px',
    position: 'relative'
  },
  body: {
    cursor: 'pointer',
    backgroundColor: 'gray',
    height: '180px',
    borderRadius: '8px 8px 0 0',
    position: 'relative',
    overflow: 'hidden',

    '&:hover $hover': {
      display: 'block',
      backgroundColor: 'rgba(0, 0, 0, 0.5)'
    }
  },
  hover: {
    display: 'none',
    width: '100%',
    height: '100%',
    borderRadius: '8px 8px 0 0',
    padding: '16px',
    position: 'absolute',
    inset: 0
  },
  hoverVisible: {
    display: 'block',
    backgroundColor: 'rgba(0, 0, 0, 0.5)'
  },
  hoverTopActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  hoverCenterActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    marginTop: '28px'
  },
  select: {
    backgroundColor: 'rgba(235, 235, 235, 1)',
    borderRadius: '6px',
    height: '32px',
    padding: '0 12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  selected: {
    backgroundColor: 'rgba(235, 235, 235, 1)',
    borderRadius: '6px',
    height: '32px',
    width: 'auto',
    padding: '0 12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    position: 'absolute',
    top: '16px',
    left: '16px'
  },
  footer: {
    padding: '12px 18px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  img: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
}))

type Props = ImageCardProps & {
  selected: boolean
  onToggleSelect: () => void
  handleOpenDetails?: (id: string) => void
  onDelete?: (id: string) => void
  onEvidenceChange?: (id: string) => void
}

const ImageCard = ({
  id,
  thumbPath,
  mid,
  templates,
  group,
  selected,
  onToggleSelect,
  handleOpenDetails,
  onDelete,
  onEvidenceChange
}: Props) => {
  const classes = useStyles()

  return (
    <div className={`shadow-s ${classes.container}`}>
      <div className={classes.body} role="button" tabIndex={0}>
        {thumbPath && <img className={classes.img} src={thumbPath} alt="img" />}
        {selected && (
          <div className={classes.selected}>
            <Checkbox checked={selected} onChange={onToggleSelect} />
            <Title size="xsmall">{selected ? 'Selected' : 'Select'}</Title>
          </div>
        )}
        <div className={classes.hover}>
          <div
            className={classes.hoverTopActions}
            onClick={e => e.stopPropagation()}
          >
            <div className={classes.select}>
              <Checkbox checked={selected} onChange={onToggleSelect} />
              <Title size="xsmall">{selected ? 'Selected' : 'Select'}</Title>
            </div>
            {onDelete && (
              <IconButton
                onClick={() => onDelete(id)}
                size="medium"
                variant="secondary"
              >
                <Delete />
              </IconButton>
            )}
          </div>
          <div
            className={classes.hoverCenterActions}
            onClick={e => e.stopPropagation()}
          >
            {handleOpenDetails && (
              <Button
                onClick={() => handleOpenDetails(id)}
                size="medium"
                className={classes.button}
                variant="primary"
              >
                <Eye /> <Title size="xsmall">View</Title>
              </Button>
            )}
            {onEvidenceChange && (
              <Button
                size="medium"
                className={classes.button}
                variant="secondary"
                onClick={() => onEvidenceChange(id)}
              >
                <Refresh /> <Title size="xsmall">Replace</Title>
              </Button>
            )}
          </div>
        </div>
      </div>
      <div className={classes.footer}>
        <Title size="small">{group}</Title>
        <Text color="primary" size="xsmall">
          {mid}
        </Text>
        <Text color="secondary" size="xsmall">
          Used in {templates?.length || 0} templates
        </Text>
      </div>
    </div>
  )
}

export default ImageCard
